// "Supplier commission statements" on a crew's detail page.
//
// The crew-scoped view of what the Finance screen shows for everyone: the
// monthly bills this crew owes DriveCars on Model 2/3 sales. Read-only here —
// issuing and paying happen on the Finance screen, which is where the audit
// trail and the whole-month context live, and this section links straight to
// it filtered by this crew.
//
// Per-currency, never merged: a crew billing in MAD and EUR has two rows a
// month, the same as everywhere else this ledger surfaces.

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { DetailSection } from '@drivecars/admin-ui';
import { fetchVendorStatements } from '@/lib/admin-api';

function cycleLabel(cycleIndex: number): string {
  const year = Math.floor(cycleIndex / 12);
  const month = cycleIndex % 12;
  return new Date(Date.UTC(year, month, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function VendorStatementsSection({ vendorId }: { vendorId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'vendor-statements', { vendorId }],
    queryFn: ({ signal }) => fetchVendorStatements({ vendorId, limit: 12 }, signal),
  });

  const rows = data?.rows ?? [];

  return (
    <DetailSection title={`Supplier commission statements (${rows.length})`}>
      {error ? (
        <div
          role="alert"
          className="mb-3 rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load statements: {(error as Error).message}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-[12.5px] text-[color:var(--bo-mute)]">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-[12.5px] text-[color:var(--bo-mute)]">
          Nothing billed to this crew yet. Statements appear once a
          supplier-commission booking is confirmed and the month is generated.
        </p>
      ) : (
        <ul className="divide-y divide-[color:var(--bo-line)]">
          {rows.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 py-2 text-[12.5px]">
              <span className="min-w-[9rem] text-[color:var(--bo-mute)]">
                {cycleLabel(s.cycleIndex)}
              </span>
              <span className="tabular-nums">
                {s.bookingCount} booking{s.bookingCount === 1 ? '' : 's'}
              </span>
              {/* The currency rides with the number, always. */}
              <span className="flex-1 text-end font-semibold tabular-nums">
                {s.amount.toFixed(2)} {s.currency}
              </span>
              <span className="capitalize text-[color:var(--bo-mute)]">{s.status}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3">
        <Link
          to={'/vendor-statements' as never}
          search={{ vendorId } as never}
          className="text-[12.5px] font-medium text-[color:var(--bo-accent)] hover:underline"
        >
          Open in Supplier commission →
        </Link>
      </div>
    </DetailSection>
  );
}
